import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { PartnerProductTablePage } from '@karehero/llama/pwa/pages/PartnerProductTablePage/PartnerProductTablePage';
import type { PartnerProductRow } from '@karehero/llama/pwa/pages/PartnerProductTablePage/PartnerProductTablePage';

import {
  useListAllPartnerProductsQuery,
  useUpdatePartnerProductMutation,
} from 'store/api/partnerProductCodes';
import { newToast } from 'store/slices/uiSlice';

export const PartnerProductTable = () => {
  const dispatch = useDispatch();
  const { data: products = [] } = useListAllPartnerProductsQuery();
  const [updateProduct] = useUpdatePartnerProductMutation();

  const handleToggle = useCallback(
    async (
      productId: string,
      field: 'requiresPartnerCode' | 'requiresLicenceCode',
      currentValue: boolean,
    ) => {
      const product = products.find((p) => p.id === productId);
      if (!product) return;

      try {
        await updateProduct({
          productId,
          requiresPartnerCode:
            field === 'requiresPartnerCode'
              ? !currentValue
              : product.requiresPartnerCode,
          requiresLicenceCode:
            field === 'requiresLicenceCode'
              ? !currentValue
              : product.requiresLicenceCode,
        }).unwrap();
      } catch {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to update partner product.',
            variant: 'error',
          }),
        );
      }
    },
    [products, updateProduct, dispatch],
  );

  const rows: PartnerProductRow[] = useMemo(
    () =>
      products.map((p) => ({
        id: p.id,
        name: p.name,
        requiresPartnerCode: p.requiresPartnerCode,
        requiresLicenceCode: p.requiresLicenceCode,
      })),
    [products],
  );

  return <PartnerProductTablePage rows={rows} onToggle={handleToggle} />;
};

export default PartnerProductTable;
